/**
 * DL-02 — the download manager: ONE per app tab, mounted in the web shell
 * above the router, so a running download outlives the dialog it was started
 * from and survives every route change. It owns the queue (`manager.ts`) and
 * runs the items one after the other (Christian 2026-10-22) with the DL-01
 * engine (`run.ts`); the dialog, the status bar and the manager modal only
 * read it and send actions.
 *
 *   enqueue(scope, bundle, root)  after the folder pick (a user gesture) — the
 *                                 item starts when nothing else is running
 *   stop(id)                      a queued item leaves the queue (resumable);
 *                                 a running one is flagged — the engine reads
 *                                 the flag before every file, the files in
 *                                 flight finish
 *   resume(id)                    a stopped / failed item re-queues; the
 *                                 verify pass skips what is on disk
 *   remove(id)                    forget a queued / stopped / done item
 *                                 (removing a running one = stop + remove)
 *   clearDone()                   the manager modal's close drops done rows
 *   pauseAll() / resumeAll()      Christian 2026-10-22: a bad hotel line —
 *                                 nothing starts, the running item finishes
 *                                 its files in flight and waits as paused;
 *                                 Resume all continues in the original order
 *   stopAll()                     every running / queued / paused item → stopped
 *
 * `beforeunload` asks while an item is running: the folder handles do not
 * survive a reload, Resume after re-opening the data set re-verifies.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  type PropsWithChildren,
  type ReactElement,
} from "react";
import { useApiOptional } from "@aeriosiq/api-client";
import type { DownloadDirLike } from "./fs.js";
import {
  aggregate,
  INITIAL_MANAGER_STATE,
  managerReducer,
  nextItemId,
  nextToRun,
  type DownloadItem,
  type ManagerAggregate,
  type ManagerState,
} from "./manager.js";
import {
  requestDaManifest,
  requestExportBundle,
  type DownloadScope,
  type ManifestBundle,
  type ManifestClient,
} from "./manifestApi.js";
import { fetchSasBytes, runImageDownload, type RunProgress } from "./run.js";

export interface DownloadManager {
  items: readonly DownloadItem[];
  totals: ManagerAggregate;
  /** Queue a picked download; returns the item id (the dialog follows it). */
  enqueue: (
    scope: DownloadScope,
    bundle: ManifestBundle,
    root: DownloadDirLike,
  ) => string;
  stop: (id: string) => void;
  resume: (id: string) => void;
  remove: (id: string) => void;
  clearDone: () => void;
  pauseAll: () => void;
  resumeAll: () => void;
  stopAll: () => void;
  /** "Pause all" is on */
  paused: boolean;
  itemOf: (id: string) => DownloadItem | undefined;
}

const DownloadManagerContext = createContext<DownloadManager | null>(null);

export interface DownloadManagerProviderProps {
  /** The manifest reader for link renewal (defaults to the host client); tests pass a stub. */
  client?: ManifestClient | undefined;
  /** The byte reader of a SAS URL (defaults to `fetch`); tests pass a stub. */
  fetchBytes?: ((url: string) => Promise<Response>) | undefined;
  /** The engine's per-run file parallelism (tests lower it). */
  concurrency?: number | undefined;
  /**
   * DL-04 — write `bundle.json` beside every data set's `manifest.json`.
   * The shell sets it for a platform admin only; the server refuses the
   * read for everyone else anyway.
   */
  writeExportBundle?: boolean | undefined;
}

export function DownloadManagerProvider({
  client,
  fetchBytes,
  concurrency,
  writeExportBundle,
  children,
}: PropsWithChildren<DownloadManagerProviderProps>): ReactElement {
  // the client is needed only for link renewal DURING a run; the shell may
  // mount the manager where no ApiProvider exists (a chrome test) — then a
  // renewal fails and the file is reported "expired", nothing else breaks
  const hostApi = useApiOptional();
  const api: ManifestClient = client ??
    hostApi ?? {
      get: () => Promise.reject(new Error("no API client mounted")),
    };
  const read = fetchBytes ?? fetchSasBytes;
  const [state, dispatch] = useReducer(managerReducer, INITIAL_MANAGER_STATE);
  // the stop flags the engine polls, per item; a ref so the running closure
  // sees the latest value without a re-render
  const stopFlags = useRef(new Map<string, boolean>());
  // "Pause all": the running item's engine reads it before every file too
  const pausedRef = useRef(false);
  pausedRef.current = state.paused;
  // the id of the item whose run is in flight — guards the StrictMode double
  // effect and a re-render between `started` and the engine's first report
  const inFlight = useRef<string | null>(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  // ── the runner: one item at a time ─────────────────────────────────────
  const next = nextToRun(state);
  const nextId = next?.id ?? null;
  useEffect(() => {
    if (nextId === null || inFlight.current !== null) return;
    const item = state.items.find((it) => it.id === nextId);
    if (!item) return;
    inFlight.current = item.id;
    stopFlags.current.set(item.id, false);
    dispatch({ type: "started", id: item.id });
    const finish = (progress: RunProgress): void => {
      inFlight.current = null;
      if (!alive.current) return;
      dispatch({ type: "finished", id: item.id, progress });
    };
    runImageDownload(item.root, item.scope, item.bundle.manifests, {
      fetchBytes: read,
      refreshManifest: (daId) => requestDaManifest(api, daId),
      onProgress: (progress) => {
        if (alive.current)
          dispatch({ type: "progress", id: item.id, progress });
      },
      shouldStop: () =>
        stopFlags.current.get(item.id) === true || pausedRef.current,
      concurrency,
      ...(writeExportBundle
        ? { fetchBundle: (daId: string) => requestExportBundle(api, daId) }
        : {}),
    })
      .then(finish)
      .catch((e: unknown) => {
        // the folder itself refused (permission revoked, a removed drive):
        // kept resumable, worded as a write failure — as the DL-01 dialog did
        finish({
          phase: "stopped",
          das: [],
          filesTotal: item.bundle.imageCount,
          filesDone: 0,
          filesPresent: 0,
          bytesWritten: 0,
          requiredBytes: item.bundle.totalBytes,
          verified: false,
          stop: {
            kind: "write_failed",
            fileName: item.root.name,
            detail: e instanceof Error ? e.message : null,
          },
          failures: [],
          linksRenewed: false,
        });
      });
    // the run is started once per queued item — the deps are the item id
    // and the stable readers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextId]);

  // ── a reload while running: ask (the handles die with the page) ─────────
  const running = state.items.some((it) => it.state === "running");
  useEffect(() => {
    if (!running) return;
    const ask = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", ask);
    return () => {
      window.removeEventListener("beforeunload", ask);
    };
  }, [running]);

  // ── the API the dialog / bar / modal use ────────────────────────────────
  const stateRef = useRef<ManagerState>(state);
  stateRef.current = state;
  const enqueue = useCallback(
    (scope: DownloadScope, bundle: ManifestBundle, root: DownloadDirLike) => {
      const id = nextItemId(stateRef.current);
      dispatch({ type: "enqueue", scope, bundle, root, at: Date.now() });
      return id;
    },
    [],
  );
  const stop = useCallback((id: string) => {
    stopFlags.current.set(id, true);
    dispatch({ type: "stop", id });
  }, []);
  const resume = useCallback((id: string) => {
    stopFlags.current.set(id, false);
    dispatch({ type: "resume", id, at: Date.now() });
  }, []);
  const remove = useCallback((id: string) => {
    stopFlags.current.set(id, true);
    dispatch({ type: "remove", id });
  }, []);
  const clearDone = useCallback(() => {
    dispatch({ type: "clearDone" });
  }, []);
  const pauseAll = useCallback(() => {
    pausedRef.current = true;
    dispatch({ type: "pauseAll" });
  }, []);
  const resumeAll = useCallback(() => {
    pausedRef.current = false;
    dispatch({ type: "resumeAll" });
  }, []);
  const stopAll = useCallback(() => {
    for (const it of stateRef.current.items)
      if (it.state === "running") stopFlags.current.set(it.id, true);
    pausedRef.current = false;
    dispatch({ type: "stopAll" });
  }, []);

  const value = useMemo<DownloadManager>(
    () => ({
      items: state.items,
      totals: aggregate(state),
      enqueue,
      stop,
      resume,
      remove,
      clearDone,
      pauseAll,
      resumeAll,
      stopAll,
      paused: state.paused,
      itemOf: (id) => state.items.find((it) => it.id === id),
    }),
    [
      state,
      enqueue,
      stop,
      resume,
      remove,
      clearDone,
      pauseAll,
      resumeAll,
      stopAll,
    ],
  );
  return (
    <DownloadManagerContext.Provider value={value}>
      {children}
    </DownloadManagerContext.Provider>
  );
}

/** The manager, or null where no provider is mounted (a pop-out window, a bare test). */
export function useDownloadManagerOptional(): DownloadManager | null {
  return useContext(DownloadManagerContext);
}

export function useDownloadManager(): DownloadManager {
  const m = useContext(DownloadManagerContext);
  if (!m)
    throw new Error(
      "useDownloadManager: no DownloadManagerProvider above — mount it in the shell (DL-02)",
    );
  return m;
}
